import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useState } from 'react';
import { AssessmentType, Checklist, Student, UnitChecklist, WeeklyChecklist } from 'types';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import { getCurriculumBenchmarkChecklists } from 'util/checklists-util';

const t = (key) => translate('ui', key);

const useStyles = makeStyles((theme: Theme) => {
  return {
    accordion: {
      '& .MuiButton-root': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        borderRadius: 0,
        borderBottom: `1px solid ${(theme.palette as any).grey[600]}`,
        fontSize: '1.2rem',
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(4),
        paddingBottom: theme.spacing(2),
        paddingRight: theme.spacing(4),

        '& .MuiSvgIcon-root': {
          fontSize: '2rem',
        },
      },
    },
    accordionBody: {
      backgroundColor: (theme.palette as any).grey[300],
      padding: theme.spacing(2),

      '&:not(last-of-type)': {
        borderBottom: `1px solid ${(theme.palette as any).grey[600]}`,
      },
    },
    panel: {
      width: '100%',
      border: `1px solid ${(theme.palette as any).grey[600]}`,
      cursor: 'pointer',
      '&:hover': {
        transform: 'scale(1.01, 1.01)',
        boxShadow: '0 1px 10px 2px rgb(0 0 0 / 20%)',
      },
      '& .MuiCardContent-root': {
        textAlign: 'center',
      },
    },
    panelLink: {
      width: '100%',
      textDecoration: 'none !important',
    },
    selected: {
      color: '#DC20AD !important',
    },
    p4: {
      padding: theme.spacing(4),
    },
  };
});

const ObservationalDropdown = (props: {
  students: Student[];
  curriculumId: number;
  classId: number;
  checklists: Checklist[];
}): React.ReactElement => {
  const [benchmarkOpen, setBenchmarkOpen] = useState<boolean>(false);
  const [unitOpen, setUnitOpen] = useState<boolean>(false);
  const [weeklyOpen, setWeeklyOpen] = useState<boolean>(false);

  const classes = useStyles();

  if (props.students.length === 0) {
    return (
      <Typography variant="body1" align="center" className={classes.p4}>
        {t('class.studentsNone')}
      </Typography>
    );
  }

  const benchmarks = getCurriculumBenchmarkChecklists(props.checklists, props.curriculumId);

  const units = props.checklists.filter(
    (checklist) => checklist.type === AssessmentType.Unit
  ) as UnitChecklist[];

  const weeklies = props.checklists.filter(
    (checklist) => checklist.type === AssessmentType.Week
  ) as WeeklyChecklist[];

  return (
    <>
      <Grid container item xs={12} className={classes.accordion}>
        {benchmarks.length > 0 && (
          <Grid item xs={12}>
            <Button
              onClick={() => setBenchmarkOpen(!benchmarkOpen)}
              className={benchmarkOpen ? classes.selected : ''}
            >
              {t('class.benchmark')}
              {benchmarkOpen ? <ExpandLess /> : <ExpandMore />}
            </Button>
            <Collapse
              in={benchmarkOpen}
              timeout="auto"
              unmountOnExit
              className={classes.accordionBody}
            >
              <Grid container item xs={12} spacing={4}>
                {benchmarks.map((benchmark, index) => (
                  <Grid key={index} container item xs={4}>
                    <Link
                      component={RouterLink}
                      to={`/checklist/${benchmark.id}?classId=${props.classId}&curriculumId=${props.curriculumId}`}
                      className={classes.panelLink}
                    >
                      <Card
                        key={props.classId + '-' + props.curriculumId + '-' + benchmark.id}
                        className={classes.panel}
                      >
                        <CardContent>
                          <Typography variant="body1">
                            {t('class.benchmarkPeriod-' + benchmark.period)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Grid>
        )}
        {units.length > 0 && (
          <Grid item xs={12}>
            <Button
              onClick={() => setUnitOpen(!unitOpen)}
              className={unitOpen ? classes.selected : ''}
            >
              {t('class.unit')}
              {unitOpen ? <ExpandLess /> : <ExpandMore />}
            </Button>
            <Collapse in={unitOpen} timeout="auto" unmountOnExit className={classes.accordionBody}>
              <Grid container item xs={12} spacing={4}>
                {units.map((unit, index) => (
                  <Grid key={index} container item xs={4}>
                    <Link
                      component={RouterLink}
                      to={`/checklist/${unit.id}?classId=${props.classId}&curriculumId=${props.curriculumId}`}
                      className={classes.panelLink}
                    >
                      <Card className={classes.panel}>
                        <CardContent>
                          <Typography variant="body1">
                            {`${t('class.unit')} ${unit.unit.unitNumber}`}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Grid>
        )}
        {weeklies.length > 0 && (
          <Grid item xs={12}>
            <Button
              onClick={() => setWeeklyOpen(!weeklyOpen)}
              className={weeklyOpen ? classes.selected : ''}
            >
              {t('class.weekly')}
              {weeklyOpen ? <ExpandLess /> : <ExpandMore />}
            </Button>
            <Collapse
              in={weeklyOpen}
              timeout="auto"
              unmountOnExit
              className={classes.accordionBody}
            >
              <Grid container item xs={12} spacing={4}>
                {weeklies.map((weekly, index) => (
                  <Grid key={index} container item xs={4}>
                    <Link
                      component={RouterLink}
                      to={`/checklist/${weekly.id}?classId=${props.classId}&curriculumId=${props.curriculumId}`}
                      className={classes.panelLink}
                    >
                      <Card
                        key={props.classId + '-' + props.curriculumId + '-' + weekly.id}
                        className={classes.panel}
                      >
                        <CardContent>
                          <Typography variant="body1">
                            {`${t('class.week')} ${weekly.weekNumber}`}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default ObservationalDropdown;
